// Admin → Import, Step 4: the run plan (`runPlan`) written by the browser, one record per call
// through the Worklist's own writer (`useWriter`, `logged-write.ts`) — list values, then lines (their
// first save as Approve makes it: the baseline, or pick-ticket copies, `ensureStored`), then pickups,
// then their shipping rows. Up to 4 records in flight, one line's writes one after another.
//
// Each record is `{ partOf }`: no System log entry of its own. A run has two: when it starts and when
// it ends (`import`, on its `import-run` record). Its counts and status are saved every 25 records,
// so a refresh leaves a record a Resume can pick up (the plan is the browser's, never stored).
//
// OUTSIDE-PLATFORM: P4 no batch or transaction: a run is calls one by one; a failed record is said
// and the run goes on — Resume (or a run again) writes what is missing.

import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  IMPORT_RUNS,
  LINE_PICK_TICKETS,
  LIST_VALUES,
  NO_HOLIDAYS,
  PICKUPS,
  PRE_ROUTES,
  ROUTING_API,
  ROUTING_LINES,
  SAVE_EVERY,
  SHIPMENTS,
  SHIPPING_ROWS,
  importLineChange,
  listValueInput,
  localToday,
  resetText,
  runRecordInput,
  shippingRowInput,
  routingRows,
  storedLineFields,
} from "@app/shared";
import type { ImportPlan, ImportRunRecord, LineView, RoutingLineRecord, RunPlan, StoreCounts } from "@app/shared";
import { apiGet, crudApi, crudErrorText, useMe } from "@axny/platform-ui";
import { routingKey, useHolidays, useRoutingData } from "./data";
import { useLoggedWrites } from "./logged-write";
import { useWriter } from "./writer";

/** Records in flight at once: measured on staging, ~0.4 s a call (`SECONDS_PER_WRITE`). */
export const RUN_WIDTH = 4;

export interface RunFailure {
  what: string;
  error: string;
}

export interface RunState {
  status: "idle" | "running" | "paused" | "done";
  done: number;
  total: number;
  current: string;
  failures: RunFailure[];
  /** The records each line, pickup and list value got: the result file's ids. */
  ids: { lines: Record<string, string>; pickups: Record<string, string>; values: Record<string, string> };
  run: ImportRunRecord | null;
}

const IDLE: RunState = {
  status: "idle",
  done: 0,
  total: 0,
  current: "",
  failures: [],
  ids: { lines: {}, pickups: {}, values: {} },
  run: null,
};

type Rec = Record<string, unknown> & { id: string; rv?: string };

/** The run's counts as its record keeps them (≤ 2000 characters). */
const countsText = (plan: RunPlan, s: Pick<RunState, "done" | "failures">, tabs: Record<string, number>) =>
  JSON.stringify({
    planned: {
      values: plan.values.length,
      lines: plan.lines.filter((l) => l.write !== "skip").length,
      pickups: plan.pickups.length,
      shippingRows: plan.pickups.reduce((n, p) => n + p.rows.length, 0),
    },
    written: s.done - s.failures.length,
    failed: s.failures.length,
    rows: tabs,
  }).slice(0, 2000);

export function useImportRun() {
  const me = useMe();
  const who = me.data?.authenticated ? me.data.user.displayName || me.data.user.username : "";
  const today = localToday();
  const data = useRoutingData("worklist");
  const holidays = useHolidays();
  const writer = useWriter(routingKey("worklist"), who, today);
  const writes = useLoggedWrites();
  const queryClient = useQueryClient();
  const [state, setState] = useState<RunState>(IDLE);
  const paused = useRef(false);
  const resumeWaiters = useRef<(() => void)[]>([]);
  const kit = writer.kit;

  const ready = !!data.data && !holidays.isPending;

  const pause = () => {
    paused.current = true;
    setState((s) => (s.status === "running" ? { ...s, status: "paused" } : s));
  };
  const resume = () => {
    paused.current = false;
    setState((s) => (s.status === "paused" ? { ...s, status: "running" } : s));
    for (const go of resumeWaiters.current.splice(0)) go();
  };
  const waitWhilePaused = () =>
    paused.current ? new Promise<void>((r) => resumeWaiters.current.push(r)) : Promise.resolve();

  /**
   * Writes `run`. `existing`: a Running / Paused run being resumed — its record is updated, its start
   * entry already written. Resolves with the final state.
   */
  const start = async (
    plan: ImportPlan,
    run: RunPlan,
    summary: { lines: string; decided: string },
    existing: ImportRunRecord | null,
  ): Promise<RunState> => {
    const payload = data.data;
    if (!payload) throw new Error("The Worklist is not loaded yet");
    const lines = routingRows(payload, today, holidays.data?.calendar ?? NO_HOLIDAYS).assembled.lines;
    const byKey = new Map<string, LineView>();
    for (const l of lines) if (!l.noWms && !byKey.has(l.groupKey)) byKey.set(l.groupKey, l);
    const startedAt = new Date().toISOString();
    const tag = `Workbook import ${existing?.id ?? startedAt.slice(0, 19)}`;
    const tabs = Object.fromEntries(
      plan.tabs.filter((t) => t.ticked).map((t) => [t.name.trim(), plan.rows.filter((r) => r.tab === t.name).length]),
    );
    const writtenLines = run.lines.filter((l) => l.write !== "skip");
    const total =
      run.values.length + writtenLines.length + run.pickups.length + run.pickups.reduce((n, p) => n + p.rows.length, 0);
    const s: RunState = { ...IDLE, status: "running", total, ids: { lines: {}, pickups: {}, values: {} } };
    paused.current = false;
    setState(s);

    // The run's record: created with the start entry, or a resumed one set Running again.
    let record: Rec;
    if (existing) {
      // Its version now: the last progress save may be newer than the list the page read.
      const now = await crudApi.get(IMPORT_RUNS.id, existing.id);
      record = await writes.update(
        IMPORT_RUNS,
        existing.id,
        String(now.rv),
        { ...now, status: "Running" },
        { partOf: tag },
      );
    } else {
      record = await writes.create(
        IMPORT_RUNS,
        runRecordInput({
          fileName: plan.file.name,
          fileHash: plan.file.hash,
          startedBy: who,
          startedAt,
          cutoff: plan.cutoff,
          tabs: Object.keys(tabs),
          counts: countsText(run, s, tabs),
        }),
        {
          action: "import",
          lineText: "Workbook import",
          changes: [],
          note: `Import of ${plan.file.name} started: ${summary.lines}`.slice(0, 500),
        },
      );
    }
    let saving = Promise.resolve();
    let sinceSave = 0;
    const saveProgress = (status: string) => {
      saving = saving.then(async () => {
        try {
          record = await writes.update(
            IMPORT_RUNS,
            record.id,
            String(record.rv),
            { ...record, status, counts: countsText(run, s, tabs) },
            { partOf: tag },
          );
        } catch {
          // The counts are kept at the next save; the run goes on.
        }
      });
      return saving;
    };

    const tick = (what: string) => {
      s.done++;
      s.current = what;
      setState({ ...s });
      if (++sinceSave >= SAVE_EVERY) {
        sinceSave = 0;
        void saveProgress(paused.current ? "Paused" : "Running");
      }
    };
    const fail = (what: string, e: unknown) => {
      s.failures.push({ what, error: e instanceof Error && !("status" in e) ? e.message : crudErrorText(e) });
      tick(what);
    };
    /** Jobs `RUN_WIDTH` at a time, each started only while the run is not paused. */
    const pool = async (jobs: { what: string; run: () => Promise<void> }[]) => {
      let next = 0;
      const worker = async () => {
        while (next < jobs.length) {
          await waitWhilePaused();
          const job = jobs[next++]!;
          s.current = job.what;
          try {
            await job.run();
            tick(job.what);
          } catch (e) {
            fail(job.what, e);
          }
        }
      };
      await Promise.all(Array.from({ length: RUN_WIDTH }, worker));
    };
    const log = { partOf: tag };

    // 1. List values added in Step 3.
    await pool(
      run.values.map((v) => ({
        what: `${v.list}: ${v.value}`,
        run: async () => {
          const rec = await kit.create(LIST_VALUES, listValueInput(v), log);
          s.ids.values[`${v.list}|${v.value}`] = rec.id;
        },
      })),
    );
    // 2. Lines: a new one is its first save, Approved by the import; one with a record gets only
    //    what Step 3 said (the file's typed fields over it, or its pickup's date).
    await pool(
      writtenLines.map((l) => ({
        what: l.place,
        run: async () => {
          const view = byKey.get(l.key);
          if (!view) throw new Error(`${l.place} is no longer on the Worklist`);
          const fields = storedLineFields(l.fields) as Partial<RoutingLineRecord>;
          const id = await kit.chained(kit.keyOf(view), async () => {
            const existingId = kit.storedId(view);
            // Planned new, saved by someone since the plan: theirs stays (Resume skips it).
            if (existingId && l.write === "create")
              throw new Error(`${l.place} was saved since the plan: not written over`);
            if (existingId) {
              kit.keep([await kit.put(ROUTING_LINES, existingId, fields, log)]);
              return existingId;
            }
            const change: Partial<RoutingLineRecord> = importLineChange(fields, l, view.fields, startedAt);
            return (await kit.ensureStored(view, change, log, undefined, l.overrides ?? undefined)).id;
          });
          s.ids.lines[l.key] = id;
        },
      })),
    );
    for (const l of run.lines) if (l.write === "skip" && l.id) s.ids.lines[l.key] ??= l.id;
    // 3. Pickups, then 4. their rows: a line's id is the one it has now.
    await pool(
      run.pickups.map((p) => ({
        what: `Pickup ${p.day} ${p.input.loadRef}`.trim(),
        run: async () => {
          const rec = await kit.create(PICKUPS, p.input, log);
          s.ids.pickups[p.entry] = rec.id;
        },
      })),
    );
    await pool(
      run.pickups.flatMap((p) =>
        p.rows.map((r, i) => ({
          what: `Pickup ${p.day}: ${r.line}`,
          run: async () => {
            const pickupId = s.ids.pickups[p.entry];
            const lineId = s.ids.lines[r.line];
            if (!pickupId) throw new Error("Its pickup was not saved");
            if (!lineId) throw new Error("Its line was not saved");
            await kit.create(SHIPPING_ROWS, shippingRowInput(r, pickupId, lineId, i), log);
          },
        })),
      ),
    );

    // The end: its entry, with the counts and what was decided.
    await saving;
    const created = writtenLines.filter((l) => l.write === "create" && s.ids.lines[l.key]);
    const end = `Imported ${plan.file.name}: ${created.filter((l) => l.source === "A2000").length} A2000 lines, ${
      created.filter((l) => l.source === "Deposco").length
    } Deposco lines, ${Object.keys(s.ids.pickups).length} pickups; ${summary.decided}; ${s.failures.length} failed`;
    try {
      record = await writes.update(
        IMPORT_RUNS,
        record.id,
        String(record.rv),
        {
          ...record,
          status: s.failures.length && s.failures.length === s.total ? "Failed" : "Done",
          finishedAt: new Date().toISOString(),
          counts: countsText(run, s, tabs),
          summary: [end, ...s.failures.slice(0, 30).map((f) => `Failed: ${f.what} — ${f.error}`)]
            .join("\n")
            .slice(0, 4000),
        },
        { action: "import", lineText: "Workbook import", changes: [], note: end.slice(0, 500) },
      );
    } catch (e) {
      s.failures.push({ what: "The run's record", error: crudErrorText(e) });
    }
    s.status = "done";
    s.current = "";
    s.run = record as unknown as ImportRunRecord;
    setState({ ...s });
    await queryClient.invalidateQueries({ queryKey: ["routing"] });
    return s;
  };

  /**
   * The Reset (staging's test data): every shipping row, shipment, pickup, pick-ticket copy, line and
   * pre-route removed, one call each (`{ partOf }`), in that order — a row before its pickup and line.
   * The System log is never removed; one entry says what went. `onProgress` after each record.
   */
  const resetAll = async (onProgress: (done: number, total: number) => void) => {
    const ids = await apiGet<
      Record<"lines" | "pickTickets" | "pickups" | "shippingRows" | "shipments" | "preRoutes", string[]>
    >(`${ROUTING_API}/admin/import/reset-records`);
    const stamp = new Date().toISOString().slice(0, 19);
    const log = { partOf: `Reset ${stamp}` };
    const order = [
      [SHIPPING_ROWS, ids.shippingRows],
      [SHIPMENTS, ids.shipments],
      [PICKUPS, ids.pickups],
      [LINE_PICK_TICKETS, ids.pickTickets],
      [ROUTING_LINES, ids.lines],
      [PRE_ROUTES, ids.preRoutes],
    ] as const;
    const total = order.reduce((n, [, list]) => n + list.length, 0);
    let done = 0;
    const failed: RunFailure[] = [];
    for (const [entity, list] of order) {
      let next = 0;
      const worker = async () => {
        while (next < list.length) {
          const id = list[next++]!;
          try {
            await kit.remove(entity, id, log);
          } catch (e) {
            failed.push({ what: `${entity.label} ${id}`, error: crudErrorText(e) });
          }
          onProgress(++done, total);
        }
      };
      await Promise.all(Array.from({ length: RUN_WIDTH }, worker));
    }
    const counts: StoreCounts = {
      lines: ids.lines.length,
      pickTickets: ids.pickTickets.length,
      pickups: ids.pickups.length,
      shippingRows: ids.shippingRows.length,
      shipments: ids.shipments.length,
      preRoutes: ids.preRoutes.length,
    };
    await writes.note(IMPORT_RUNS, "reset", {
      action: "remove",
      lineText: "Routing data",
      changes: [],
      note: `${resetText(who, counts)}${failed.length ? ` · ${failed.length} could not be removed` : ""}`.slice(0, 500),
    });
    await queryClient.invalidateQueries({ queryKey: ["routing"] });
    return failed;
  };

  return { ready, state, start, pause, resume, resetAll, reset: () => setState(IDLE) };
}
